// Mobile sticky ATC bar — bridges to the canonical product form's
// add-to-cart button. All cart logic (debounce, drawer open, error
// handling) is owned by custom-cart.js; this just dispatches a click
// on the canonical button so the existing flow runs.
//
// Price / image / availability are sourced from window.product.variants
// (set in snippets/product.liquid) rather than scraped from the visible
// price block, so the sticky stays in sync regardless of whether the
// haven picker, a standard radio picker, or a select changed the variant.

(function() {
  function getProduct() {
    return (typeof window !== 'undefined' && window.product) || null;
  }

  function buildVariantsById() {
    const product = getProduct();
    const map = {};
    if (product && Array.isArray(product.variants)) {
      product.variants.forEach(function(v) { map[String(v.id)] = v; });
    }
    return map;
  }

  function findCanonicalAddBtn() {
    return document.querySelector(
      'form.shopify-product-form [data-cart-add], ' +
      'form.shopify-product-form .btn.product-form__cart-submit, ' +
      'form.shopify-product-form [name="add"], ' +
      'form.shopify-product-form .add-to-cart, ' +
      'form[action*="/cart/add"] [name="add"], ' +
      'form[action*="/cart/add"] .add-to-cart'
    );
  }

  function findVariantSelect() {
    return document.querySelector(
      'form.shopify-product-form select[name="id"], ' +
      'form[action*="/cart/add"] select[name="id"]'
    );
  }

  // Broader than findVariantSelect: single-variant products carry the id in a
  // hidden input, not a <select>.
  function findVariantInput() {
    return document.querySelector(
      'form.shopify-product-form [name="id"], ' +
      'form[action*="/cart/add"] [name="id"]'
    );
  }

  function currentVariantId() {
    const input = findVariantInput();
    if (input && input.value) return String(input.value);
    const product = getProduct();
    if (product && Array.isArray(product.variants) && product.variants.length === 1) {
      return String(product.variants[0].id);
    }
    return null;
  }

  // How many of this variant are already in the cart. Sums across lines —
  // one variant can occupy several lines via properties or bundles.
  function cartQtyForVariant(variantId) {
    const cart = window.modernCart && window.modernCart.cart;
    if (!variantId || !cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function(total, item) {
      return String(item.variant_id) === String(variantId)
        ? total + (item.quantity || 0)
        : total;
    }, 0);
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    const product = getProduct();
    const fmt = (product && product.money_format) ||
                (window.theme && window.theme.moneyFormat) ||
                undefined;
    if (window.Shopify && Shopify.formatMoney) {
      try { return Shopify.formatMoney(cents, fmt); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function img120(url) {
    if (!url) return url;
    // Shopify CDN already-sized URLs use ?width=N&height=N. Replace if
    // present, otherwise append (preserving any existing query string).
    if (/[?&]width=\d+/.test(url)) {
      url = url.replace(/([?&])width=\d+/, '$1width=120');
      url = url.replace(/([?&])height=\d+/, '$1height=120');
      if (!/[?&]crop=/.test(url)) url += '&crop=center';
    } else {
      url += (url.indexOf('?') >= 0 ? '&' : '?') + 'width=120&height=120&crop=center';
    }
    return url;
  }

  function updatePrice(stickyPrice, variant) {
    if (!stickyPrice || !variant) return;
    const current = formatMoney(variant.price);
    let html = '<span class="mobile-sticky-atc__price-current">' + current + '</span>';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += '<span class="mobile-sticky-atc__price-compare">' + formatMoney(variant.compare_at_price) + '</span>';
    }
    stickyPrice.innerHTML = html;
  }

  function updateImage(stickyImg, variant) {
    if (!stickyImg) return;
    let src = null;
    if (variant && variant.featured_image && variant.featured_image.src) {
      src = variant.featured_image.src;
    } else {
      const product = getProduct();
      if (product) {
        if (typeof product.featured_image === 'string') {
          src = product.featured_image;
        } else if (product.featured_image && product.featured_image.src) {
          src = product.featured_image.src;
        }
      }
    }
    if (!src) return;
    stickyImg.src = img120(src);
  }

  function isBackorderVariant(variantId) {
    const product = getProduct();
    return !!(product && product.backorder && product.backorder[variantId]);
  }

  function updateButtonAvailability(stickyBtn, variant) {
    if (!stickyBtn || !variant) return;
    // Skip if the canonical button is currently in a transient state
    // (Adding... / Added ✓) — let syncStateFromCanonical own the text.
    if (stickyBtn.classList.contains('is-loading') ||
        stickyBtn.classList.contains('is-success')) return;

    if (variant.available || isBackorderVariant(variant.id)) {
      stickyBtn.disabled = false;

      // Already in the cart. Tapping "Add to Cart" on an item that is already
      // there silently doubled the line in an observed session ($399 → $798)
      // with no cue that anything was already in the cart, and the shopper
      // then had to hunt for the drawer's decrease control. Offer the drawer
      // instead — quantity can be raised there deliberately.
      const inCart = cartQtyForVariant(variant.id);
      if (inCart > 0) {
        stickyBtn.dataset.stickyAction = 'view';
        stickyBtn.classList.add('is-in-cart');
        stickyBtn.textContent = inCart > 1 ? 'In cart (' + inCart + ') · View' : 'In cart · View';
        return;
      }

      stickyBtn.dataset.stickyAction = 'add';
      stickyBtn.classList.remove('is-in-cart');
      stickyBtn.textContent = variant.available ? 'Add to Cart' : 'Pre-order';
      return;
    }

    stickyBtn.disabled = true;
    stickyBtn.dataset.stickyAction = 'add';
    stickyBtn.classList.remove('is-in-cart');
    stickyBtn.textContent = 'Sold Out';
  }

  function syncFromVariant(refs, variant) {
    if (!variant) return;
    updatePrice(refs.price, variant);
    updateImage(refs.img, variant);
    updateButtonAvailability(refs.btn, variant);
  }

  function syncStateFromCanonical(stickyBtn, canonicalBtn, variantsById, variantSelect) {
    if (!canonicalBtn) {
      stickyBtn.disabled = true;
      return;
    }
    const txt = (canonicalBtn.textContent || '').trim();
    if (/Adding/i.test(txt)) {
      stickyBtn.textContent = 'Adding...';
      stickyBtn.classList.add('is-loading');
      stickyBtn.classList.remove('is-success');
    } else if (/Added/i.test(txt) || /✓/.test(txt)) {
      stickyBtn.textContent = 'Added ✓';
      stickyBtn.classList.remove('is-loading');
      stickyBtn.classList.add('is-success');
    } else {
      // Idle — restore availability text from current variant data.
      stickyBtn.classList.remove('is-loading', 'is-success');
      // Fall back to the form's id field: single-variant products have no
      // <select>, so this branch used to find no variant and leave the button
      // stuck on whatever the transient state last wrote ("Added ✓").
      const bySelect = variantSelect && variantsById[String(variantSelect.value)];
      const id = currentVariantId();
      const v = bySelect || (id && variantsById[id]);
      if (v) {
        updateButtonAvailability(stickyBtn, v);
      } else {
        stickyBtn.disabled = !!canonicalBtn.disabled;
      }
    }
  }

  function init() {
    const bar = document.querySelector('.mobile-sticky-atc');
    if (!bar || bar.dataset.init === '1') return;
    bar.dataset.init = '1';

    const refs = {
      btn: bar.querySelector('[data-mobile-sticky-atc-btn]'),
      price: bar.querySelector('[data-mobile-sticky-atc-price]'),
      img: bar.querySelector('[data-mobile-sticky-atc-image]')
    };
    if (!refs.btn) return;

    const variantsById = buildVariantsById();
    const variantSelect = findVariantSelect();

    // 1500ms in-flight lock. Session replays show shoppers double-tapping
    // this button on mobile (e.g. one observed session added the same item
    // twice within 1462ms then removed one), so we drop the lock window in
    // line with the observed gap. Optimistic visual state (Adding...) also
    // confirms the first tap registered so a second tap feels unnecessary.
    let inFlight = false;
    refs.btn.addEventListener('click', function(e) {
      e.preventDefault();
      // In-cart state: show them what they already have rather than adding
      // a second one they did not ask for.
      if (refs.btn.dataset.stickyAction === 'view') {
        if (window.modernCart && typeof window.modernCart.openCart === 'function') {
          window.modernCart.openCart();
        }
        return;
      }
      if (inFlight) return;
      const canonicalBtn = findCanonicalAddBtn();
      if (!canonicalBtn || canonicalBtn.disabled || refs.btn.disabled) return;
      inFlight = true;
      refs.btn.classList.add('is-loading');
      refs.btn.textContent = 'Adding...';
      canonicalBtn.click();
      setTimeout(function() { inFlight = false; }, 1500);
    });

    const canonicalBtn = findCanonicalAddBtn();
    if (canonicalBtn) {
      const observer = new MutationObserver(function() {
        syncStateFromCanonical(refs.btn, canonicalBtn, variantsById, variantSelect);
      });
      observer.observe(canonicalBtn, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class']
      });
    }

    if (variantSelect) {
      variantSelect.addEventListener('change', function() {
        const v = variantsById[String(variantSelect.value)];
        if (v) syncFromVariant(refs, v);
      });
    }

    // Haven picker dispatches this with detail.variant — use it directly
    // (avoids any timing dependency on its updatePriceDisplay running first).
    document.addEventListener('variant:changed', function(e) {
      const detailV = e && e.detail && e.detail.variant;
      const full = detailV && variantsById[String(detailV.id)];
      syncFromVariant(refs, full || detailV);
    });

    // The cart loads asynchronously, so on first paint we do not yet know
    // whether this variant is already in it. custom-cart-v6 fires this on
    // every render (optimistic and canonical) — re-evaluate the button then.
    document.addEventListener('cart:updated', function() {
      const id = currentVariantId();
      const v = id && variantsById[id];
      if (v) updateButtonAvailability(refs.btn, v);
    });

    // Initial sync — in case the server-rendered first variant differs
    // from the canonical button state we have to mirror.
    if (variantSelect) {
      const v = variantsById[String(variantSelect.value)];
      if (v) syncFromVariant(refs, v);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
